// External Libraries
import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectorRef,
  CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { Observable, Subscription, combineLatest, of, switchMap } from 'rxjs';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLinkActive,
  RouterLink,
  RouterOutlet
} from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';

// Frontegg Services
import { FronteggAppService, FronteggAuthService } from '@frontegg/angular';

// Project Services
import { AuthService } from '../../services/auth/auth.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { SignOutService } from '../../services/sign-out-state/sign-out-state.service';
import { UserPrivilegesService } from '../../services/auth-privileges/auth-privileges.service';
import { WhatFixService } from '../../services/whatFix/whatfix.service';
import { WebCaptureUploadComponent } from './web-capture-upload/web-capture-upload.component';
import { TenantApiService } from '../../services/tenant-api/tenant-api.service';
import { BannerComponent } from '../../components/banner/banner.component';
import { Bind } from 'primeng/bind';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { PrimeTemplate } from 'primeng/api';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { UrlVerificationComponent } from '@ai-document-processor/shared-libraries';

/** Route the printer opens to link. Its query string is kept across login, and its tenant is restored from the Content URL. */
const PRINTER_AUTHORIZE_PATH = '/webcapture/printer/authorize';

@Component({
  selector: 'app-web-capture',
  templateUrl: './web-capture.component.html',
  styleUrls: ['./web-capture.component.scss'],
  imports: [
    BannerComponent,
    RouterLinkActive,
    RouterLink,
    RouterOutlet,
    Bind,
    ConfirmDialog,
    PrimeTemplate,
    Button,
    TranslatePipe,
    UrlVerificationComponent,
    Tooltip
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class WebCaptureComponent implements OnInit, OnDestroy {
  private fronteggAuthService = inject(FronteggAuthService);
  private fronteggAppService = inject(FronteggAppService);
  private authService = inject(AuthService);
  private featureFlagService = inject(FeatureFlagService);
  private router = inject(Router);
  private signOutService = inject(SignOutService);
  private userPrivilegesService = inject(UserPrivilegesService);
  private whatFix = inject(WhatFixService);
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private cdr = inject(ChangeDetectorRef);
  tenantService = inject(TenantApiService);

  private subscription = new Subscription();
  private signOutSubscription = new Subscription();

  isAccessEnabled: boolean | null = null;
  isAccessFlagLoading: boolean = true;
  featureFlagErrorText: string = '';
  userId: string = '';
  isLoading = true;
  loadingSubscription: Subscription;
  user?: any;
  tenantId: string = '';
  vanityName: string = '';
  isUrlVerified: boolean = false;
  currentTab: string = '';

  get bannerTitleKey(): string {
    return this.isVirtualPrinterOnly() ? 'printerHeader' : 'webCapture';
  }
  get isRestrictedRouteForVirtualPrinter(): boolean {
    return (
      this.router.url.includes('/batches') &&
      (this.isAccessFlagLoading || this.isVirtualPrinterOnly())
    );
  }
  transcriptProcessingFlag = signal(false);
  isAccessIDPMgmt: boolean = false;
  isAccessITPAdmin: boolean = false;
  isWebApps = true;
  isAutoClassificationEnabled: boolean = false;
  userPrincipalId: string = '';
  hasWebCaptureAccess = signal(true);
  webCaptureFeatureFlag = signal(true);
  isVirtualPrinterOnly = signal(false);

  constructor() {
    const fronteggAppService = this.fronteggAppService;

    this.loadingSubscription = fronteggAppService.isLoading$.subscribe(
      isLoading => (this.isLoading = isLoading)
    );
  }

  /**
   * Handles the `window:load` event by disabling scroll on the body.
   */
  @HostListener('window:load')
  onLoad() {
    document.body.style.overflow = 'hidden';
  }

  /**
   * Angular lifecycle hook that runs after component initialization.
   * Sets up routing events, feature flag checks, and sign-out logic.
   */
  ngOnInit(): void {
    document.body.style.overflow = 'hidden';

    const redirectRoute = localStorage.getItem('redirectAfterLogin');
    if (redirectRoute?.includes('/transcripts')) {
      this.router.navigateByUrl(redirectRoute);
      return;
    }

    this.handleCachedParams();

    this.subscription.add(
      this.route.queryParams.subscribe(params => this.handleQueryParams(params))
    );

    this.updateCurrentTabFromUrl(this.router.url);

    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.updateCurrentTabFromUrl(this.router.url);
          this.redirectIfRestrictedForVirtualPrinter();
        }
      })
    );
  }

  /**
   * Sends Virtual Printer only users to Unauthorized when they land on batching routes, once the access flags have finished loading.
   */
  private redirectIfRestrictedForVirtualPrinter(): void {
    if (
      !this.isAccessFlagLoading &&
      this.isVirtualPrinterOnly() &&
      this.router.url.includes('/batches')
    ) {
      this.router.navigate(['unauthorized'], {
        state: { displayName: this.user?.customClaims?.display_name }
      });
    }
  }

  /**
   * Handles cached query parameters from window object.
   */
  private handleCachedParams(): void {
    const cachedParams = (window as any)._queryParamsCache;
    if (cachedParams && (cachedParams.tenantId || cachedParams.vanityName)) {
      this.tenantId = cachedParams.tenantId;
      this.vanityName = cachedParams.vanityName;

      if (this.tenantId) {
        localStorage.setItem('tenantId', this.tenantId);
      }
      if (this.vanityName) {
        localStorage.setItem('vanityName', this.vanityName);
      }
    }
  }

  /**
   * Handles query parameters for tenant identification.
   * @param params - The query parameters from the URL
   */
  handleQueryParams(params: any): void {
    this.tenantId = this.getQueryParamCaseInsensitive(params, 'tenantId') || '';
    this.vanityName = this.getQueryParamCaseInsensitive(params, 'vanityName') || '';
    if (this.tenantId) {
      localStorage.setItem('tenantId', this.tenantId);
    }
    if (this.vanityName) {
      localStorage.setItem('vanityName', this.vanityName);
    }

    //we will only ever send the tenantId and need to go through alternate pathways in this case
    if (this.isPrinterRoute()) {
      if (!this.vanityName) {
        const vanityFromStorage = localStorage.getItem('vanityName');
        this.vanityName =
          vanityFromStorage ??
          this.vanityNameFromContentUrl(localStorage.getItem('contentURL') ?? '') ??
          '';
        if (!vanityFromStorage && this.vanityName) {
          localStorage.setItem('vanityName', this.vanityName);
        }
      }
      if (this.tenantId && this.vanityName) {
        this.validateTenantInfo();
      } else {
        this.checkFeatureFlag();
      }
    } else {
      if (this.tenantId || this.vanityName) {
        const urlWithoutParams = this.location.path().split('?')[0];
        this.location.replaceState(urlWithoutParams);
      }

      if ((this.tenantId && !this.vanityName) || (!this.tenantId && this.vanityName)) {
        console.error('Both tenantId and vanityName must be provided together.');
        this.clearTenantInfo();
      }

      if (this.tenantId && this.vanityName) {
        this.validateTenantInfo();
      } else {
        // No tenant params, proceed with existing localStorage values
        this.checkFeatureFlag();
      }
    }

    this.signOutSubscription = this.signOutService.signOut$.subscribe(signOut => {
      if (signOut && !this.signOutService.isFilingContent) {
        this.signOutService.signOut();
      }
    });
  }

  /**
   * Gets the vanity name from a Content URL, the same way the URL verification screen does.
   * @param contentUrl - The stored Etrieve Content URL
   * @returns The vanity name, or undefined if the URL is not recognized
   */
  private vanityNameFromContentUrl(contentUrl: string): string | undefined {
    const url = contentUrl.toLocaleLowerCase();
    return url.includes('/content')
      ? url.split('https://')[1]?.split('.')[0]
      : url.split('https://')[1]?.split('content')[0];
  }

  /**
   * Validates tenant information by cross-referencing tenantId and vanityName.
   */
  private validateTenantInfo(): void {
    this.subscription.add(
      this.tenantService.getTenantIdFromVanity(this.vanityName).subscribe({
        next: response => {
          if (response.tenantID === this.tenantId) {
            this.subscription.add(
              this.tenantService.getTenantUrls(this.tenantId).subscribe({
                next: tenantUrls => {
                  localStorage.setItem('contentURL', tenantUrls.contentUrl);
                  this.checkFeatureFlag();
                },
                error: error => {
                  console.error('Error fetching tenant URLs:', error);
                  this.checkFeatureFlag();
                }
              })
            );
          } else {
            console.error('Invalid tenant: vanity name mismatch');
            this.clearTenantInfo();
          }
        },
        error: error => {
          console.error('Invalid tenant information', error);
          this.clearTenantInfo();
        }
      })
    );
  }

  /**
   * Updates the currentTab value based on the current URL
   * @param url - The current router URL
   */
  private updateCurrentTabFromUrl(url: string): void {
    if (url.includes('/batches')) {
      this.currentTab = 'batches';
    } else {
      this.currentTab = '';
    }
  }

  /**
   * Run the feature flag check for the provided tenantId
   * @param tenantId the provided tenantId
   * @param userId principal ID
   */
  runFeatureFlagCheck(tenantId: string, userId: string): void {
    this.isAccessFlagLoading = true;
    this.featureFlagService
      .initialize({ userId, tenantId })
      .then(() => {
        this.subscription.add(
          this.featureFlagService
            .getFlag('license-itp', false)
            .pipe(
              switchMap(itpFlagValue => {
                this.transcriptProcessingFlag.set(itpFlagValue);
                return combineLatest([
                  this.featureFlagService.getFlag('web-capture-access', false),
                  this.featureFlagService.getFlag('virtual-printer-access', false)
                ]);
              }),
              switchMap(([flagValue, virtualPrinterFlagValue]) => {
                const virtualPrinterOnly = !!virtualPrinterFlagValue && !flagValue;
                this.isVirtualPrinterOnly.set(virtualPrinterOnly);
                if (virtualPrinterOnly) {
                  this.webCaptureFeatureFlag.set(false);
                  this.transcriptProcessingFlag.set(false);
                }

                if (!flagValue && !virtualPrinterOnly) {
                  this.denyAccess('webCaptureFeatureFlagError');
                  return of(null);
                }

                if (!this.isPrinterRoute()) {
                  this.allowAccess();
                  this.redirectIfRestrictedForVirtualPrinter();
                  return of(null);
                }

                // The printer must be enabled before the login starts, so a tenant without it is stopped here
                if (virtualPrinterFlagValue) {
                  this.allowAccess();
                } else {
                  this.denyAccess('printerAuthFeatureDisabled');
                }
                return of(null);
              })
            )
            .subscribe()
        );
      })
      .catch(() => {
        this.isAccessFlagLoading = false;
        this.isUrlVerified = false;
        this.isAccessEnabled = false;
      });
  }

  /**
   * @returns true when the current route is the printer authorization route
   */
  private isPrinterRoute(): boolean {
    return this.location.path().split('?')[0] === PRINTER_AUTHORIZE_PATH;
  }

  /**
   * Lets the user in: starts the Frontegg login and the in-app guidance.
   */
  private allowAccess(): void {
    this.isAccessFlagLoading = false;
    this.isAccessEnabled = true;
    this.startFronteggAuth();
    this.whatFix.initializeWithContext(this.tenantId, this.userId);
  }

  /**
   * Stops before any login and shows the access message on the Content URL screen.
   * @param messageKey - Translation key of the message to show
   */
  private denyAccess(messageKey: string): void {
    this.isAccessFlagLoading = false;
    this.isUrlVerified = false;
    this.isAccessEnabled = false;
    this.featureFlagErrorText = messageKey;
  }

  /**
   * Re-verifies the tenant URL and triggers feature flag check based on the tenant ID
   */
  onSuccessfulVerification(): void {
    const tenantId = localStorage.getItem('tenantId');

    if (tenantId) {
      this.isAccessFlagLoading = true;
      this.runFeatureFlagCheck(tenantId, this.userPrincipalId);
    }
  }

  /**
   * Check the feature flag based on tenantId from localStorage.
   */
  checkFeatureFlag(): void {
    const tenantId = localStorage.getItem('tenantId');
    this.subscription.add(
      this.fronteggAuthService.authState$.subscribe(authState => {
        if (authState.user) {
          this.user = authState.user;
          this.userPrincipalId = this.user.customClaims?.principal_id;
        }
      })
    );

    if (tenantId) {
      this.runFeatureFlagCheck(tenantId, this.userPrincipalId);
    } else {
      this.isAccessFlagLoading = false;
      this.isUrlVerified = false;
      this.isAccessEnabled = null;
    }
  }
  // TODO: Get Feature Flag Working
  /**
   * Initializes the transcript processing feature flag.
   * Enables or disables UI elements depending on flag value.
   */
  initializeTranscriptProcessingFlag(): void {
    const tenantId = localStorage.getItem('tenantId');
    if (tenantId) {
      this.featureFlagService.getFlag('license-itp', false).subscribe(flagValue => {
        this.transcriptProcessingFlag.set(flagValue && !this.isVirtualPrinterOnly());
      });
    }
  }

  //  Get Feature Flag license-IDP
  /**
   * Initializes the auto-classification feature flag and sets
   * `isAccessIDPMgmt` based on the user's roles and flag status.
   */
  initializeAutoClassificationFlag(): void {
    const tenantId = localStorage.getItem('tenantId');
    if (tenantId) {
      this.featureFlagService.getFlag('license-IDP', false).subscribe(flagValue => {
        this.isAutoClassificationEnabled = flagValue as boolean;
        this.isAccessIDPMgmt = this.checkPlatformAdmin(this.user.roles) && flagValue;
      });

      this.featureFlagService.getFlag('license-itp', false).subscribe(flagValue => {
        this.isAutoClassificationEnabled = flagValue as boolean;
        this.isAccessITPAdmin = this.hasItpAdminRole() && flagValue;
      });
    }
  }

  /**
   * Starts Frontegg Authentication flow
   */
  startFronteggAuth() {
    this.isUrlVerified = true;
    const vanityName = localStorage.getItem('vanityName');
    this.tenantId = localStorage.getItem('tenantId') || '';

    if (this.tenantId && vanityName) {
      this.subscription.add(
        this.fronteggAuthService.authState$.subscribe(authState => {
          if (!authState.isAuthenticated && !this.isLoading) {
            this.loginWithRedirect(this.tenantId, vanityName);
          } else {
            if (authState.user?.accessToken) {
              const tokenTenantId = authState.user.tenantId;
              if (tokenTenantId !== this.tenantId) {
                this.signOut();
                return;
              }

              this.user = authState.user;
              this.authService.setAuthToken(this.user?.accessToken);

              // Fetch privileges immediately so child components don't wait on feature flags
              this.userPrivilegesService
                .getUserPrivileges(this.user.accessToken)
                .then(privileges => {
                  const hasWebCapturePrivilege = privileges.applicationPrivileges.some(
                    p => p.code === 'WEB_CAPTURE_ACCESS'
                  );

                  const redirectRoute = localStorage.getItem('redirectAfterLogin');

                  if (redirectRoute && redirectRoute !== '/webcapture') {
                    localStorage.removeItem('redirectAfterLogin');
                    this.navigateToRedirectRoute(redirectRoute);
                  }

                  if (!hasWebCapturePrivilege && !this.isVirtualPrinterOnly()) {
                    this.router.navigate(['unauthorized'], {
                      state: { displayName: this.user.customClaims.display_name }
                    });
                  } else {
                    this.hasWebCaptureAccess.set(true);
                  }

                  this.initializeAutoClassificationFlag();
                  this.initializeTranscriptProcessingFlag();
                  this.cdr.detectChanges();
                });
            }
          }
        })
      );
    } else {
      this.isUrlVerified = false;
    }
  }

  /**
   * Redirects the user to the login page with the specified tenant id and vanityName
   * @param tenantId - the id of the tenant to log in to
   * @param vanityName - the vanityName name associated with the tenant
   */
  loginWithRedirect(tenantId: string, vanityName: string): void {
    const [urlWithoutParams, query] = this.location.path().split('?');

    // The printer's PKCE challenge and state must survive the login round trip. Other query
    // parameters (tenant info) are deliberately dropped.
    const keepQuery = urlWithoutParams === PRINTER_AUTHORIZE_PATH && !!query;
    localStorage.setItem(
      'redirectAfterLogin',
      keepQuery ? `${urlWithoutParams}?${query}` : urlWithoutParams
    );

    this.fronteggAuthService.loginWithRedirect({
      tenantId: tenantId,
      organization: vanityName
    });
  }

  /**
   * Navigates to the route saved before login. Saved routes can carry a query string, which
   * `navigate` would encode into the path, so those go through `navigateByUrl`.
   * @param route - The saved route, optionally with a query string
   */
  private navigateToRedirectRoute(route: string): void {
    if (route.includes('?')) {
      this.router.navigateByUrl(route);
    } else {
      this.router.navigate([route]);
    }
  }

  /**
   * ITP Processor = College Processor Role (legacy FrontEgg key)
   * ITP HS Processor = High School Processor Role
   * Checks if user has any ITP Processor roles
   */
  hasItpAccess(): boolean {
    return this.userPrivilegesService.hasItpProcessorRole(this.user?.roles);
  }

  /**
   * Checks if user has ITP Admin role
   */
  hasItpAdminRole(): boolean {
    return this.userPrivilegesService.hasItpAdminRole(this.fronteggAuthService.user?.roles);
  }

  /**
   * Signs out the user of the application and redirects them back to the log in page
   */
  signOut(): void {
    this.userPrivilegesService.clearCache();
    this.signOutService.setSignOutState(true);
  }

  /**
   * Navigates to the corresponding route based on the tab.
   * @param tab - The selected tab string
   */
  goToTab(tab: string) {
    this.navigateToTab(tab);
  }

  /**
   * Updates the URL to reflect the selected tab without reloading the page.
   * @param tab - The selected tab string
   */
  navigateToTab(tab: string) {
    if (tab === '') {
      this.router.navigate(['/webcapture']);
    } else {
      this.router.navigate(['/webcapture', tab]);
    }
  }

  /**
   * Angular lifecycle hook that runs when the component is destroyed.
   * Cleans up subscriptions and resets the body scroll.
   */
  ngOnDestroy(): void {
    document.body.style.overflow = '';
    this.loadingSubscription.unsubscribe();
    this.subscription.unsubscribe();
    this.signOutSubscription.unsubscribe();
  }

  /**
   * Checks whether the current user has permission to perform the given action.
   * @param actionCode - Privilege code to check
   * @returns Observable<boolean> indicating permission
   */
  canPerformAction(actionCode: string): Observable<boolean> {
    return this.userPrivilegesService.canPerformAction(actionCode);
  }

  /**
   * Determines whether the user has the 'Platform Admin' role.
   * @param roles - List of roles from the user's auth state
   * @returns true if 'Platform Admin' role is found, false otherwise
   */
  checkPlatformAdmin(roles: Array<{ key: string }>): boolean {
    return roles.some(role => role.key === 'Platform Admin');
  }

  /**
   * Clears tenant information from localStorage and resets access flags.
   */
  clearTenantInfo() {
    localStorage.removeItem('tenantId');
    localStorage.removeItem('vanityName');
    localStorage.removeItem('contentURL');
    this.tenantId = '';
    this.vanityName = '';
    this.isAccessEnabled = false;
    this.isAccessFlagLoading = false;
  }

  /**
   *
   * @param params - URL query parameters
   * @param paramName - Name of the parameter to retrieve
   */
  private getQueryParamCaseInsensitive(params: any, paramName: string): string | null {
    const key = Object.keys(params).find(k => k.toLowerCase() === paramName.toLowerCase());

    return key ? params[key] : null;
  }
}
